import {Box, Typography} from '@viptech/react-components'
import moment from 'moment'
import {useEffect, useState} from 'react'
import 'rsuite/dist/rsuite.min.css'
import CustomCalendar from 'src/components/customCalendar/CustomCalendar'
import Dropdown from 'src/components/dropdown/Dropdown'
import {DropdownItem} from 'src/components/dropdown/DropdownProps'
import {MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectScrollView, MultiSelectValue, SelectValue} from 'src/components/multi-select/MultiSelect'
import {useCompany} from 'src/contexts/CompanyContext'
import useCameras from 'src/hooks/api/camera/useCameras'
import useClients from 'src/hooks/api/client/useClients'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import ExportDataButton from 'src/pages/dashboard/components/ExportDataButton'
import PersonalizedDatesRange, {RangeOfDates} from 'src/pages/dashboard/components/PersonalizedDatesRange'

type Props = {
  dashboardSelected: 'events' | 'detections' | 'analytic' | 'synthetic'
  filter: {cameraIds: Array<number>; clientIds: Array<number>; start: string; end: string}
  setDashboardSelected: (value: 'analytic' | 'synthetic') => void
  initialDates: [Date, Date]
  onChangeFilter: (start: string, end: string, clientIds?: Array<number>, cameraIds?: Array<number>) => void
  setRangeDatesFilter: (range: RangeOfDates) => void
  setLabelOnFilter: (label: string) => void
}

function PersonalizedHeader({filter, initialDates, dashboardSelected, setDashboardSelected, onChangeFilter, setRangeDatesFilter, setLabelOnFilter}: Props) {
  const {selectedCompanyId, setSelectedCompanyId, companies} = useCompany()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const {data: clients, isLoading: isLoadingClients} = useClients({selectedCompanies: [selectedCompanyId], includes: ['cameras']})
  const [clientSelected, setClientSelected] = useState<Array<SelectValue>>([])

  const {data: cameras, isLoading: isLoadingCameras} = useCameras({
    clientIds: clientSelected?.map((it) => Number(it.value)),
  })
  const [camerasIdsSelected, setCamerasIdsSelected] = useState<Array<SelectValue>>([])

  const [dateFilter, setDateFilter] = useState<{start: Date; end: Date}>({start: initialDates[0], end: initialDates[1]})
  const [companySeleted, setCompanySelected] = useState<DropdownItem>()

  useEffect(() => {
    const company = companies?.find((com) => com.id === selectedCompanyId)
    if (company) {
      setSelectedCompanyId(company.id)
      setCompanySelected({id: String(company.id), label: company.name})
    }
  }, [companies])

  useEffect(() => {
    const clientIds = clientSelected.map((client) => Number(client.value))
    const start = moment(dateFilter.start).format()
    const end = moment(dateFilter.end).format()
    const cameraIds = camerasIdsSelected.map((camera) => Number(camera.value))
    onChangeFilter(start, end, clientIds, cameraIds)
  }, [clientSelected, dateFilter, camerasIdsSelected])

  useEffect(() => {
    if (clients?.data.data.entities.length) setClientSelected([{value: String(clients.data.data.entities[0].id), label: clients.data.data.entities[0].name}])
  }, [selectedCompanyId, clients])

  useEffect(() => {
    if (cameras?.data.data.entities.length)
      setCamerasIdsSelected(
        cameras?.data.data.entities.map((camera) => {
          return {
            value: String(camera.id),
            label: camera.name,
          }
        }),
      )
    else setCamerasIdsSelected([])
  }, [cameras])

  return (
    <Box padding='30px 10px' display='flex' align='center' justifyContent='space-between' backgroundColor='#ffff' borderRadius='16px' margin='20px'>
      <Box display='flex' align='center' columnGap='24px'>
        <Box height={'40px'} borderRadius={'8px'} padding='0px 4px' display='flex' align='center' justifyContent='center' border='2px solid #D0D5DD'>
          <Box
            display='flex'
            direction='column'
            align='center'
            justifyContent='center'
            borderRadius={'4px'}
            padding={'6px 16px'}
            onClick={() => setDashboardSelected('analytic')}
            backgroundColor={dashboardSelected === 'analytic' ? styledTheme.colors.primary : styledTheme.colors.white}
            style={{cursor: 'pointer'}}>
            <Typography
              variant='label'
              color={dashboardSelected === 'analytic' ? styledTheme.colors.white : '#98A2B3'}
              weight={700}
              size='14px'
              cursor='pointer'
              onClick={() => setDashboardSelected('analytic')}>
              Analítico
            </Typography>
          </Box>
          <Box
            display='flex'
            direction='column'
            align='center'
            justifyContent='center'
            borderRadius={'4px'}
            padding={'6px 24px'}
            onClick={() => setDashboardSelected('synthetic')}
            backgroundColor={dashboardSelected === 'synthetic' ? styledTheme.colors.primary : styledTheme.colors.white}
            style={{cursor: 'pointer'}}>
            <Typography
              variant='label'
              color={dashboardSelected === 'synthetic' ? styledTheme.colors.white : '#98A2B3'}
              weight={700}
              size='14px'
              cursor='pointer'
              onClick={() => setDashboardSelected('synthetic')}>
              Sintético
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box display='flex' align='center' columnGap='20px' justifyContent='flex-end'>
        {dashboardSelected === 'analytic' && (
          <>
            <Dropdown
              width='250px'
              height='40px'
              borderColor='#9aa4b2'
              fontSize='14px'
              placeholder='Empresa'
              selected={companySeleted}
              onChangeSelected={(e) => {
                setCompanySelected(e)
                setSelectedCompanyId(Number(e.id))
              }}
              showFloatingLabel
              items={companies.map((it) => {
                return {
                  id: String(it.id),
                  label: it.name,
                }
              })}
            />

            <MultiSelect onChange={setClientSelected} value={clientSelected} loading={isLoadingClients}>
              <MultiSelectValue placeholder={'Selecione um cliente'} borderColor='#9aa4b2' width='19.5em' showFloatingLabel />
              <MultiSelectContent>
                <MultiSelectScrollView>
                  {clients?.data.data.entities.map((item, index) => (
                    <MultiSelectItem key={index} value={item.id.toString()} label={item.name}>
                      {item.name}
                    </MultiSelectItem>
                  ))}
                </MultiSelectScrollView>
              </MultiSelectContent>
            </MultiSelect>

            <MultiSelect onChange={setCamerasIdsSelected} value={camerasIdsSelected} loading={isLoadingCameras} disabled={!clientSelected.length}>
              <MultiSelectValue placeholder={'Selecione uma câmera'} borderColor='#9aa4b2' width='19.5em' showFloatingLabel />
              <MultiSelectContent>
                <MultiSelectScrollView>
                  {cameras?.data.data.entities.map((item, index) => (
                    <MultiSelectItem key={index} value={item.id.toString()} label={item.name}>
                      {item.name}
                    </MultiSelectItem>
                  ))}
                </MultiSelectScrollView>
              </MultiSelectContent>
            </MultiSelect>

            <CustomCalendar
              initialDates={[dateFilter.start, dateFilter.end]}
              setDate={(dates) => setDateFilter({start: dates[0], end: dates[1]})}
              hideRuler
              hideTime
            />
          </>
        )}

        {dashboardSelected === 'synthetic' && <PersonalizedDatesRange onChangeFilter={setRangeDatesFilter} setLabelOnFilter={setLabelOnFilter} />}
        {dashboardSelected === 'analytic' && (
          <ExportDataButton
            dashboardSelected={dashboardSelected}
            start={filter.start}
            end={filter.end}
            filter={{cameraIds: filter.cameraIds, clientIds: filter.clientIds}}
          />
        )}
      </Box>
    </Box>
  )
}

export default PersonalizedHeader
